import {
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchInvoiceWatermarks } from './fetchInvoiceWatermarks';
import { type InvoiceWatermarksResponse } from './types';

/**
 * Generates reusable query options for fetching invoice watermark names.
 *
 * @param options - Additional options for the query.
 * @returns The query options including query key, query function, and settings.
 *
 * @see https://app.dev.vestigas.com/redoc
 */
export const getInvoiceWatermarksQueryOptions = <
  TData = InvoiceWatermarksResponse,
>(
  options?: Omit<
    UseQueryOptions<InvoiceWatermarksResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchInvoiceWatermarks({ signal }),
    queryKey: queryKeysInvoiceReprocessing.getWatermarks(),
    staleTime: ms('1h'),
    ...options,
  });

/**
 * Fetches invoice watermark names grouped by color with TanStack Query.
 *
 * @param options - Additional options for the query.
 * @returns The invoice watermark query result.
 *
 * @see https://app.dev.vestigas.com/redoc
 */
// fallow-ignore-next-line unused-export -- public data-layer hook for invoice watermark consumers
export const useQueryInvoiceWatermarks = <TData = InvoiceWatermarksResponse>(
  options?: Omit<
    UseQueryOptions<InvoiceWatermarksResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getInvoiceWatermarksQueryOptions<TData>(options));
